import React, { useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { useBenchmarkMetrics } from "@/hooks/api/use-benchmark-metrics";
import {
  type CampaignMetrics,
  useCampaignMetrics,
  useCampaignStatuses,
} from "@/hooks/api/use-campaign-metrics";
import { useCampaignFilterState } from "@/hooks/use-campaign-filter-state";
import { useCampaignSelection } from "@/hooks/use-campaign-selection";
import { useCampaignStatusSelection } from "@/hooks/use-campaign-status-selection";
import { useWipReportCardMetricsEnabled } from "@/hooks/use-feature-flags";
import { BenchmarkToggle } from "./benchmark-toggle";
import {
  DEFAULT_METRIC_ORDER,
  DRILLDOWN_LIMIT,
  HIDDEN_METRICS,
  WIP_METRICS,
} from "./campaign-metrics-config";
import { CampaignStatusSelector } from "./campaign-status-selector";
//import { CampaignProgression } from "./campaign-progression";
import { CampaignMetricCards } from "./metric-cards";
import { MetricDrilldown } from "./metric-drilldown";

interface CampaignScorecardProps {
  benchmarkConfig?: {
    filters?: Record<string, unknown>;
    excludeCurrentCampaign?: boolean;
    averageMetrics?: Record<string, number | string | null>;
    mode?: "dynamic" | "static";
    selectedCampaignIds?: string[];
  };
  isBenchmarkEnabled?: boolean;
  hasBenchmarkConfig?: boolean;
  onBenchmarkToggleChange?: (show: boolean) => void;
  onOpenBenchmarkModal?: () => void;
  requiresStatus?: boolean;
}

export function CampaignScorecard({
  benchmarkConfig = {},
  isBenchmarkEnabled = false,
  hasBenchmarkConfig = false,
  onBenchmarkToggleChange,
  onOpenBenchmarkModal,
  requiresStatus = true,
}: CampaignScorecardProps = {}) {
  const { selectedCampaignId } = useCampaignSelection();
  const { selectedStatuses, auto, hasOverride } =
    useCampaignStatusSelection(selectedCampaignId);
  const [selectedMetric, setSelectedMetric] = useState<
    keyof CampaignMetrics | null
  >(null);
  const wipMetricsEnabled = useWipReportCardMetricsEnabled();

  // Structured filters from new builder
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  } = useCampaignFilterState("campaigns::explorer");

  // Use DEFAULT_METRIC_ORDER and filter out WIP metrics if feature flag is disabled, then add required hidden metrics
  const metricsToFetch = React.useMemo(
    () => [
      ...new Set([
        ...(wipMetricsEnabled
          ? DEFAULT_METRIC_ORDER
          : DEFAULT_METRIC_ORDER.filter(
              (metric) => !WIP_METRICS.includes(metric),
            )),
        ...HIDDEN_METRICS,
      ]),
    ],
    [wipMetricsEnabled],
  );

  // Fetch server defaults to honor when user hasn't overridden
  const { data: statusesData } = useCampaignStatuses(
    selectedCampaignId || undefined,
  );
  const defaultEngagementStatuses = React.useMemo(() => {
    const statuses = statusesData?.statuses || [];
    return statuses.filter((s) => s.represents_engagement).map((s) => s.status);
  }, [statusesData]);

  const effectiveStatuses = React.useMemo(() => {
    if (auto) return defaultEngagementStatuses;
    if (hasOverride) return selectedStatuses; // may be empty (intentional)
    return defaultEngagementStatuses;
  }, [auto, hasOverride, selectedStatuses, defaultEngagementStatuses]);

  const shouldShowMetrics =
    !!selectedCampaignId && (!requiresStatus || effectiveStatuses.length > 0);

  // Get regular campaign metrics
  const { data: metrics, loadingStates } = useCampaignMetrics({
    campaignId: selectedCampaignId || null,
    metrics: metricsToFetch,
    statuses: effectiveStatuses,
    filters: {
      ...(hasAccountFilters && {
        account_filters: accountFilters,
      }),
      ...(hasOpportunityFilters && {
        opportunity_filters: opportunityFilters,
      }),
      ...(hasTouchpointFilters && {
        touchpoint_filters: touchpointFilters,
      }),
    },
    enabled: shouldShowMetrics,
  });

  // ALWAYS use the API benchmark metrics - no stored metrics
  const { data: benchmarkMetrics, loadingStates: apiBenchmarkLoadingStates } =
    useBenchmarkMetrics({
      campaignId: selectedCampaignId || null,
      metrics: metricsToFetch,
      benchmarkFilters: {
        ...benchmarkConfig?.filters,
        metric_filters: {
          ...(benchmarkConfig?.filters?.metric_filters || {}),
          ...(hasAccountFilters && {
            account_filters: accountFilters,
          }),
          ...(hasOpportunityFilters && {
            opportunity_filters: opportunityFilters,
          }),
          ...(hasTouchpointFilters && {
            touchpoint_filters: touchpointFilters,
          }),
        },
      },
      // Only apply excludeCurrentCampaign in dynamic mode
      excludeCurrentCampaign:
        benchmarkConfig?.mode === "dynamic" &&
        (benchmarkConfig?.excludeCurrentCampaign ?? true),
      enabled: isBenchmarkEnabled && shouldShowMetrics,
      selectedCampaignIds:
        benchmarkConfig?.mode === "static" &&
        benchmarkConfig?.selectedCampaignIds
          ? benchmarkConfig.selectedCampaignIds
          : [],
      mode: benchmarkConfig?.mode || "static",
    });

  return (
    <div className="space-y-4">
      <CampaignMetricCards
        metrics={metrics}
        onMetricSelect={setSelectedMetric}
        loadingStates={loadingStates}
        benchmarkMetrics={isBenchmarkEnabled ? benchmarkMetrics : undefined}
        benchmarkLoadingStates={
          isBenchmarkEnabled ? apiBenchmarkLoadingStates : undefined
        }
        showBenchmark={isBenchmarkEnabled}
        leftControls={
          selectedCampaignId ? (
            <div className="flex flex-wrap items-center gap-2">
              {requiresStatus && (
                <CampaignStatusSelector
                  campaignId={selectedCampaignId || undefined}
                />
              )}
              <BenchmarkToggle
                showBenchmarks={isBenchmarkEnabled}
                setShowBenchmarks={onBenchmarkToggleChange || (() => {})}
                onConfigureClick={onOpenBenchmarkModal || (() => {})}
                hasBenchmarkConfig={hasBenchmarkConfig}
              />
            </div>
          ) : null
        }
        showBody={shouldShowMetrics}
        emptyBody={
          !shouldShowMetrics ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <EmptyState
                title="Select qualifying engagement statuses"
                description="Choose one or more statuses to see engagement metrics."
              />
            </div>
          ) : undefined
        }
      />

      {/* <CampaignProgression /> */}

      {selectedMetric && (
        <MetricDrilldown
          open={!!selectedMetric}
          onOpenChange={(open) => !open && setSelectedMetric(null)}
          campaignId={selectedCampaignId || null}
          metric={selectedMetric}
          statuses={effectiveStatuses}
          drillDownLimit={DRILLDOWN_LIMIT}
          filters={{
            ...(hasAccountFilters && {
              account_filters: accountFilters,
            }),
            ...(hasOpportunityFilters && {
              opportunity_filters: opportunityFilters,
            }),
            ...(hasTouchpointFilters && {
              touchpoint_filters: touchpointFilters,
            }),
          }}
          metrics={metrics}
          loadingStates={loadingStates}
          benchmarkMetrics={isBenchmarkEnabled ? benchmarkMetrics : undefined}
          showBenchmark={isBenchmarkEnabled}
        />
      )}
    </div>
  );
}
